<template>
    <div>
        <template v-if="!hidePreview">
            <button class="btn btn-outline-secondary btn-sm" @click="openModal">
                <i class="fa fa-square-o"></i> {{ boxes.length > 0 ? 'Edit Target Areas (' + boxes.length + ')' : 'Draw Target Areas' }}
            </button>

            <!-- Inline preview of existing boxes -->
            <div v-if="boxes.length > 0" class="target-preview mt-2" @click="openModal" title="Click to edit target areas">
                <div class="target-preview-inner">
                    <img :src="background" draggable="false" />
                    <div
                        v-for="(box, index) in boxes"
                        :key="'preview-' + index"
                        class="target-box"
                        :style="getBoxStyle(box)"
                    >
                        <span class="box-label">{{ index + 1 }}</span>
                    </div>
                </div>
            </div>
        </template>

        <div ref="modal" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="modal-dialog modal-xl" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Draw Target Advertising Areas</h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="instructions mb-3">
                            <p class="mb-1"><strong>Click and drag</strong> to draw a box around each advertising surface you want to target.</p>
                            <p class="mb-0 text-muted small">The AI will place the ad only on the surfaces you mark.</p>
                        </div>

                        <div
                            class="target-canvas"
                            ref="canvas"
                            @mousedown="startDrawing"
                            @mousemove="updateDrawing"
                            @mouseup="endDrawing"
                            @mouseleave="endDrawing"
                        >
                            <img
                                ref="image"
                                :src="background"
                                @load="onImageLoad"
                                draggable="false"
                                crossorigin="anonymous"
                            />

                            <!-- Existing boxes -->
                            <div
                                v-for="(box, index) in boxes"
                                :key="index"
                                class="target-box"
                                :style="getBoxStyle(box)"
                            >
                                <span class="box-label">{{ index + 1 }}</span>
                                <button
                                    class="box-delete"
                                    @click.stop="removeBox(index)"
                                    title="Remove"
                                >
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>

                            <!-- Box being drawn -->
                            <div
                                v-if="isDrawing && currentBox"
                                class="target-box drawing"
                                :style="getBoxStyle(currentBox)"
                            ></div>
                        </div>

                        <div v-if="boxes.length > 0" class="mt-3 text-muted small">
                            {{ boxes.length }} target{{ boxes.length !== 1 ? 's' : '' }} selected. Click the X on a box to remove it.
                        </div>

                        <div v-else class="text-center text-muted py-4">
                            Click and drag on the image to draw target boxes
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
                        <button
                            type="button"
                            class="btn btn-primary"
                            @click="saveBoxes"
                            :disabled="!isValid"
                        >
                            Save ({{ boxes.length }} target{{ boxes.length !== 1 ? 's' : '' }})
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Hidden canvas for generating annotated image -->
        <canvas ref="annotationCanvas" style="display: none;"></canvas>
    </div>
</template>

<script>
export default {
    props: {
        background: {
            type: String,
            required: true,
        },
        value: {
            type: Array,
            default: () => [],
        },
        hidePreview: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            boxes: [],
            isDrawing: false,
            startPoint: null,
            currentBox: null,
            imageWidth: 0,
            imageHeight: 0,
        };
    },
    computed: {
        isValid() {
            return true;
        },
    },
    mounted() {
        this.boxes = JSON.parse(JSON.stringify(this.value || []));
    },
    watch: {
        value: {
            handler(newVal) {
                this.boxes = JSON.parse(JSON.stringify(newVal || []));
            },
            deep: true,
        },
    },
    methods: {
        openModal() {
            $(this.$refs.modal).modal('show');
        },
        closeModal() {
            $(this.$refs.modal).modal('hide');
        },
        onImageLoad() {
            this.imageWidth = this.$refs.image.naturalWidth;
            this.imageHeight = this.$refs.image.naturalHeight;
        },
        getRelativePosition(event) {
            const rect = this.$refs.canvas.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width;
            const y = (event.clientY - rect.top) / rect.height;
            return {
                x: Math.max(0, Math.min(1, x)),
                y: Math.max(0, Math.min(1, y))
            };
        },
        startDrawing(event) {
            this.isDrawing = true;
            this.startPoint = this.getRelativePosition(event);
            this.currentBox = {
                x1: this.startPoint.x,
                y1: this.startPoint.y,
                x2: this.startPoint.x,
                y2: this.startPoint.y,
            };
        },
        updateDrawing(event) {
            if (!this.isDrawing || !this.startPoint) return;

            const pos = this.getRelativePosition(event);
            this.currentBox = {
                x1: Math.min(this.startPoint.x, pos.x),
                y1: Math.min(this.startPoint.y, pos.y),
                x2: Math.max(this.startPoint.x, pos.x),
                y2: Math.max(this.startPoint.y, pos.y),
            };
        },
        endDrawing() {
            if (!this.isDrawing || !this.currentBox) {
                this.isDrawing = false;
                return;
            }

            // Only add if box has meaningful size (at least 2% in both dimensions)
            const width = this.currentBox.x2 - this.currentBox.x1;
            const height = this.currentBox.y2 - this.currentBox.y1;

            if (width > 0.02 && height > 0.02) {
                this.boxes.push({ ...this.currentBox });
            }

            this.isDrawing = false;
            this.startPoint = null;
            this.currentBox = null;
        },
        removeBox(index) {
            this.boxes.splice(index, 1);
        },
        getBoxStyle(box) {
            return {
                left: (box.x1 * 100) + '%',
                top: (box.y1 * 100) + '%',
                width: ((box.x2 - box.x1) * 100) + '%',
                height: ((box.y2 - box.y1) * 100) + '%',
            };
        },
        generateAnnotatedImage() {
            return new Promise((resolve, reject) => {
                const canvas = this.$refs.annotationCanvas;
                const ctx = canvas.getContext('2d');
                const img = this.$refs.image;

                // Set canvas to image dimensions
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;

                // Draw the original image
                ctx.drawImage(img, 0, 0);

                // Draw boxes with bright colored outlines
                this.boxes.forEach((box, index) => {
                    const x = box.x1 * canvas.width;
                    const y = box.y1 * canvas.height;
                    const w = (box.x2 - box.x1) * canvas.width;
                    const h = (box.y2 - box.y1) * canvas.height;

                    // Draw thick colored rectangle
                    ctx.strokeStyle = '#FF0000';
                    ctx.lineWidth = Math.max(4, canvas.width * 0.005);
                    ctx.strokeRect(x, y, w, h);

                    // Draw number label
                    const fontSize = Math.max(20, canvas.width * 0.03);
                    ctx.font = `bold ${fontSize}px Arial`;
                    ctx.fillStyle = '#FF0000';
                    ctx.fillText((index + 1).toString(), x + 5, y + fontSize + 5);
                });

                resolve(canvas.toDataURL('image/jpeg', 0.9));
            });
        },
        async saveBoxes() {
            // Generate annotated image
            let annotatedImage = null;
            if (this.boxes.length > 0) {
                annotatedImage = await this.generateAnnotatedImage();
            }

            // Emit both the box data and the annotated image
            this.$emit('input', this.boxes);
            this.$emit('annotated-image', annotatedImage);

            this.closeModal();
        },
    },
};
</script>

<style scoped>
.target-canvas {
    position: relative;
    cursor: crosshair;
    user-select: none;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
}

.target-canvas img {
    width: 100%;
    display: block;
}

.target-box {
    position: absolute;
    border: 3px solid #FF0000;
    background: rgba(255, 0, 0, 0.1);
    pointer-events: none;
}

.target-box:not(.drawing) {
    pointer-events: auto;
}

.target-box.drawing {
    border-style: dashed;
    background: rgba(255, 0, 0, 0.2);
}

.box-label {
    position: absolute;
    top: 2px;
    left: 2px;
    background: #FF0000;
    color: white;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: bold;
    border-radius: 2px;
}

.box-delete {
    position: absolute;
    top: 2px;
    right: 2px;
    background: #FF0000;
    color: white;
    border: none;
    padding: 2px 6px;
    font-size: 10px;
    cursor: pointer;
    border-radius: 2px;
}

.box-delete:hover {
    background: #CC0000;
}

.instructions {
    background: #f8f9fa;
    padding: 10px 15px;
    border-radius: 4px;
}

.target-preview {
    cursor: pointer;
    max-width: 400px;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
    transition: border-color 0.2s;
}

.target-preview:hover {
    border-color: #007bff;
}

.target-preview-inner {
    position: relative;
}

.target-preview-inner img {
    width: 100%;
    display: block;
}
</style>
